import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useResetPassword,
  useSetUserActive,
  useUpdateUser,
  useVerifyTutor,
} from '../../api/admin';
import { useAuth } from '../../auth/useAuth';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import type { AdminUserListItem } from '../../types/api';

export function EditUserModal({
  user,
  open,
  onClose,
}: {
  user: AdminUserListItem;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateUser(user.id);
  const { notify } = useToast();
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [timezone, setTimezone] = useState(user.timezone);
  const [errors, setErrors] = useState<string[]>([]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    // Send only what actually changed — the API rejects an empty patch, and
    // a no-op field would still show up as a change in the audit log.
    const patch: Record<string, string> = {};
    if (fullName !== user.fullName) patch.fullName = fullName.trim();
    if (email !== user.email) patch.email = email.trim();
    if (phone !== (user.phone ?? '')) patch.phone = phone.trim();
    if (timezone !== user.timezone) patch.timezone = timezone.trim();

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    try {
      await update.mutateAsync(patch);
      notify('Details updated', 'success');
      onClose();
    } catch (err) {
      setErrors(
        err instanceof ApiError
          ? err.details.length
            ? err.details
            : [err.message]
          : ['Could not save these changes.'],
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit details"
      description="Changes are recorded in the audit log with their previous values."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="edit-user"
            className="btn btn-primary"
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-user" onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label">Full name</span>
            <input
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Email</span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Phone</span>
            <input
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+20 123 456 7890"
            />
          </label>
          <label className="block">
            <span className="label">Timezone</span>
            <input
              className="input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="Africa/Cairo"
            />
          </label>
        </div>

        {errors.length > 0 && (
          <ul
            role="alert"
            className="space-y-1 rounded-md border border-danger/30 bg-danger-wash px-3 py-2 text-xs text-ink"
          >
            {errors.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </form>
    </Modal>
  );
}

export function ResetPasswordModal({
  user,
  open,
  onClose,
}: {
  user: AdminUserListItem;
  open: boolean;
  onClose: () => void;
}) {
  const reset = useResetPassword(user.id);
  const { notify } = useToast();
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setPassword('');
    setReason('');
    setError(null);
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await reset.mutateAsync({ newPassword: password, reason: reason.trim() || undefined });
      notify(`Password set for ${user.fullName}`, 'success');
      close();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set the password.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Set a new password"
      description={`${user.fullName} will be able to sign in with this immediately.`}
      width="sm"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            form="reset-password"
            className="btn btn-primary"
            disabled={reset.isPending || password.length < 8}
          >
            {reset.isPending ? 'Setting…' : 'Set password'}
          </button>
        </>
      }
    >
      <form id="reset-password" onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="label">New password</span>
          <input
            className="input"
            type="text"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
          />
          <span className="mt-1.5 block text-[0.6875rem] text-ink-3">
            Shown in plain text so you can read it out. It is never written to the
            audit log.
          </span>
        </label>

        <label className="block">
          <span className="label">Reason (optional)</span>
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Called support, identity verified"
          />
        </label>

        {error && (
          <p
            role="alert"
            className="rounded-md border border-danger/30 bg-danger-wash px-3 py-2 text-xs text-ink"
          >
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

export function ImpersonateModal({
  user,
  open,
  onClose,
}: {
  user: AdminUserListItem;
  open: boolean;
  onClose: () => void;
}) {
  const { startImpersonation } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setPending(true);
    setError(null);
    try {
      await startImpersonation(user.id, reason.trim());
      notify(`Now acting as ${user.fullName}`, 'success');
      navigate('/acting-as', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start impersonation.');
      setPending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Act as ${user.fullName}`}
      description="You will see the platform exactly as they do, for 30 minutes."
      width="sm"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={start}
            disabled={pending || !reason.trim()}
          >
            {pending ? 'Starting…' : 'Start acting as them'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="label">Why are you doing this?</span>
          <textarea
            className="textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ticket #4821 — student cannot see their booking"
          />
          <span className="mt-1.5 block text-[0.6875rem] text-ink-3">
            Required here. It is stored on the audit entry, which is what makes
            looking at someone's account defensible later.
          </span>
        </label>

        <ul className="space-y-1 rounded-md border border-rule bg-surface-2 px-3 py-2.5 text-[0.6875rem] text-ink-2">
          <li>Your admin session is kept and restored when you stop.</li>
          <li>You cannot change their password or reach this console while acting as them.</li>
          <li>Starting and stopping are both written to the audit log.</li>
        </ul>

        {error && (
          <p
            role="alert"
            className="rounded-md border border-danger/30 bg-danger-wash px-3 py-2 text-xs text-ink"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

export function SuspendDialog({
  user,
  open,
  onClose,
}: {
  user: AdminUserListItem;
  open: boolean;
  onClose: () => void;
}) {
  const setActive = useSetUserActive(user.id);
  const { notify } = useToast();
  const [reason, setReason] = useState('');
  const suspending = user.isActive;

  const confirm = async () => {
    try {
      await setActive.mutateAsync({
        isActive: !user.isActive,
        reason: reason.trim() || undefined,
      });
      notify(suspending ? `Suspended ${user.fullName}` : `Restored ${user.fullName}`, 'success');
      setReason('');
      onClose();
    } catch (err) {
      notify(
        err instanceof ApiError ? err.message : 'Could not change this account.',
        'error',
      );
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={confirm}
      danger={suspending}
      pending={setActive.isPending}
      title={suspending ? `Suspend ${user.fullName}?` : `Restore ${user.fullName}?`}
      description={
        suspending
          ? 'They are signed out immediately — any session they have open stops working on its next request. Their bookings are left as they are.'
          : 'They will be able to sign in again right away.'
      }
      confirmLabel={suspending ? 'Suspend account' : 'Restore account'}
      reason={reason}
      onReasonChange={setReason}
      reasonLabel="Reason"
      reasonPlaceholder={suspending ? 'Repeated no-shows' : 'Appeal upheld'}
    />
  );
}

export function VerifyDialog({
  user,
  open,
  onClose,
}: {
  user: AdminUserListItem;
  open: boolean;
  onClose: () => void;
}) {
  const verify = useVerifyTutor(user.id);
  const { notify } = useToast();
  const [reason, setReason] = useState('');
  const isVerified = Boolean(user.tutorProfile?.isVerified);

  const confirm = async () => {
    try {
      await verify.mutateAsync({ isVerified: !isVerified, reason: reason.trim() || undefined });
      notify(
        isVerified ? `Removed verification from ${user.fullName}` : `Verified ${user.fullName}`,
        'success',
      );
      setReason('');
      onClose();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update verification.', 'error');
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={confirm}
      pending={verify.isPending}
      danger={isVerified}
      title={isVerified ? 'Remove verification?' : `Verify ${user.fullName}?`}
      // Spec 32 moved the badge behind an approved application: the API now
      // refuses to grant it to a tutor whose documents nobody has checked.
      // This dialog stays for one release so a bookmarked workflow keeps
      // working on tutors who are already approved, and says where the real
      // decision lives.
      description={
        isVerified
          ? 'Their profile will no longer show as verified, and they drop out of verified-only search results.'
          : 'Only a tutor with an approved application can be verified. Approve them from Applications, which also sets their commission.'
      }
      confirmLabel={isVerified ? 'Remove verification' : 'Verify tutor'}
      reason={reason}
      onReasonChange={setReason}
      reasonPlaceholder={isVerified ? 'Certificate expired' : 'Teaching certificate checked'}
    />
  );
}
